// chat_width — la largeur du fil, quand l'opérateur la règle lui-même.
//
// ── CE QUE ÇA REMPLACE ──────────────────────────────────────────────────────
//
// La colonne du fil était figée par le CSS : `lg:w-[clamp(380px,22vw,520px)]`, soit entre
// 380 et 520 px selon la fenêtre, sans prise. Demande de l'opérateur : pouvoir l'élargir,
// et que le réglage soit retenu.
//
// ── UNE SEULE RÈGLE, ET C'EST ELLE QUI COMPTE ───────────────────────────────
//
// L'aperçu ne disparaît jamais. Tirer la poignée jusqu'au bord laisserait sinon un aperçu
// de zéro pixel — or l'aperçu EST ce que l'opérateur est venu regarder. Le reste (le
// plancher du fil, une valeur de stockage abîmée) découle de la même posture : on borne,
// on ne devine pas.
//
// ── PUR, PARCE QUE C'EST LA SEULE FAÇON DE L'ÉPROUVER ───────────────────────
//
// platform-ui n'a pas de harnais de test de composants : tous ses tests portent sur des
// modules purs. Une règle écrite dans le composant ne serait protégée par rien.

/** Sous ce seuil, une bulle n'affiche plus une ligne lisible (24 px de marge de chaque côté). */
export const CHAT_MIN_PX = 320;

/** Ce qu'on laisse TOUJOURS à l'aperçu. Une app jugée dans 200 px ne se juge pas. */
export const PREVIEW_MIN_PX = 360;

const estNombre = (n: unknown): n is number => typeof n === "number" && Number.isFinite(n);

/**
 * PURE. La largeur réellement applicable, bornée des deux côtés.
 *
 * Quand la fenêtre est trop étroite pour contenir les deux minimums, le PLANCHER DU FIL
 * gagne : une colonne illisible serait pire qu'un aperçu serré, et cette disposition en
 * deux colonnes n'existe de toute façon qu'au-dessus de 1024 px.
 */
export function clampChatWidth(souhaitee: number, largeurFenetre: number): number {
  if (!estNombre(largeurFenetre) || largeurFenetre <= 0) return CHAT_MIN_PX;
  if (!estNombre(souhaitee)) return CHAT_MIN_PX;
  const plafond = Math.max(CHAT_MIN_PX, largeurFenetre - PREVIEW_MIN_PX);
  return Math.min(plafond, Math.max(CHAT_MIN_PX, Math.round(souhaitee)));
}

/**
 * PURE. Ce que le stockage du navigateur rend, lu sans rien supposer.
 *
 * `null` veut dire « aucun réglage » — l'opérateur n'a jamais tiré la poignée, et la
 * colonne garde sa règle CSS d'origine. C'est ce qui rend ce changement invisible tant
 * qu'on n'y touche pas.
 *
 * Une valeur abîmée rend `null` plutôt qu'un nombre de repli : le stockage est partagé
 * avec tout ce que l'opérateur fait sur ce domaine, et une valeur d'une version
 * précédente peut y traîner. Elle n'est PAS bornée ici — `clampChatWidth` le fait au
 * moment de l'appliquer, avec la largeur de fenêtre du moment, qui n'est pas la même
 * qu'au jour où la valeur a été écrite.
 */
export function readStoredWidth(brut: string | null | undefined): number | null {
  // Un seul contrôle, et c'est volontaire. La première version commençait par écarter
  // `null`, `undefined` et la chaîne vide ; une mutation a montré que ce garde ne pouvait
  // pas tirer : `Number(null)` vaut 0, `Number("")` vaut 0 et `Number(undefined)` vaut
  // NaN — la ligne ci-dessous les refusait déjà tous les trois. Un garde qui ne peut pas
  // tirer est un second mécanisme pour un seul invariant.
  const n = Number(brut);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.floor(n);
}
